/* Homepage markup, ported verbatim from the standalone marketing site.
   Rendered inside a <div className="cw"> via dangerouslySetInnerHTML;
   interactions are wired up by homeRuntime.js. */
export const HOME_HTML = `
  <!-- ================= NAVIGATION ================= -->
  <header class="nav" id="nav">
    <div class="nav__inner">
      <a class="wordmark" href="/" aria-label="Cowork Development home">
        c<span class="wordmark__dot" aria-hidden="true"></span>work<span class="wordmark__suffix">Development</span>
      </a>
      <nav class="nav__links" aria-label="Primary">
        <a href="#services">What we build</a>
        <a href="#work">How we work</a>
        <a href="#engineers">For engineers</a>
      </nav>
      <div class="nav__cta">
        <a class="btn btn--solid" href="#contact">Start a project</a>
      </div>
      <button class="nav__toggle" id="nav-toggle" type="button" aria-label="Open menu" aria-expanded="false" aria-controls="mobile-menu">
        <span></span><span></span><span></span>
      </button>
    </div>
  </header>

  <!-- Mobile navigation -->
  <div class="mobile-menu" id="mobile-menu">
    <nav class="mobile-menu__links" aria-label="Mobile">
      <a href="#services">What we build</a>
      <a href="#work">How we work</a>
      <a href="#engineers">For engineers</a>
      <a href="#contact">Contact</a>
    </nav>
    <a class="btn btn--solid btn--lg" href="#contact">Start a project</a>
  </div>

  <main id="top">
    <!-- ================= HERO ================= -->
    <section class="hero">
      <canvas class="hero__canvas" id="network" aria-hidden="true"></canvas>
      <div class="hero__glow" aria-hidden="true"></div>
      <div class="hero__inner">
        <p class="eyebrow reveal">AI Product Engineering Partner</p>
        <h1 class="hero__title">
          <span class="hero__line">
            <span class="reveal-word" style="animation-delay:.05s">The</span>
            <span class="reveal-word" style="animation-delay:.11s">engineering</span>
            <span class="reveal-word" style="animation-delay:.17s">team</span>
          </span>
          <span class="hero__line">
            <span class="reveal-word" style="animation-delay:.23s">behind</span>
            <span class="reveal-word" style="animation-delay:.29s">your</span>
            <span class="reveal-word accent" style="animation-delay:.35s">AI&nbsp;product.</span>
          </span>
        </h1>
        <p class="hero__sub reveal">
          Ship the AI features your product needs — built by experienced engineers
          from a global network who join your team and stay accountable from first
          prototype to production.
        </p>
        <div class="hero__actions reveal">
          <a class="btn btn--solid btn--lg" href="#contact">Start a project</a>
          <a class="btn btn--ghost btn--lg" href="#work">See how we work</a>
        </div>
        <p class="hero__note reveal">Talk to an engineer, not a salesperson.</p>
        <ul class="hero__chips reveal" aria-label="How we work">
          <li>Senior engineers</li>
          <li>Embedded in your team</li>
          <li>From prototype to production</li>
        </ul>
      </div>
    </section>

    <!-- ================= SERVICES ================= -->
    <section class="services" id="services">
      <div class="section__head">
        <p class="eyebrow reveal">What we build</p>
        <h2 class="section__title reveal">AI at the core. Full&#8209;stack around it.</h2>
        <p class="section__lead reveal">
          AI is the center of what we build — with the product, cloud, and mobile
          engineering to ship it all the way into production.
        </p>
      </div>

      <article class="card card--feature reveal">
        <div class="feature__main">
          <div class="feature__top">
            <div class="card__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="3"></circle>
                <circle cx="12" cy="3.5" r="1.4"></circle>
                <circle cx="20" cy="16" r="1.4"></circle>
                <circle cx="4" cy="16" r="1.4"></circle>
                <path d="M12 5v4M13.8 13.3l4.9 2.1M10.2 13.3l-4.9 2.1"></path>
              </svg>
            </div>
            <span class="card__tag">Our core</span>
          </div>
          <h3 class="feature__title">AI Product Engineering</h3>
          <p class="feature__body">
            We build AI into products people actually use — LLM applications, agents,
            and automation that ship to production and stay reliable. Not experiments.
            Real features your users depend on.
          </p>
        </div>
        <ul class="feature__list">
          <li>LLM apps &amp; copilots</li>
          <li>AI agents &amp; automation</li>
          <li>Production-grade, not prototypes</li>
        </ul>
      </article>

      <div class="cards cards--support">
        <article class="card reveal">
          <div class="card__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="3" y="4" width="18" height="14" rx="2"></rect>
              <path d="M3 8h18M7 12h6M7 15h4"></path>
            </svg>
          </div>
          <h3 class="card__title">Product Engineering</h3>
          <p class="card__body">
            SaaS, web, and enterprise systems built to scale — with the ownership of
            a team that stays.
          </p>
        </article>

        <article class="card reveal">
          <div class="card__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <path d="M7 18a4 4 0 1 1 .5-7.97A5 5 0 0 1 17.5 11 3.5 3.5 0 0 1 17 18H7Z"></path>
              <path d="M9.5 14.5 12 12l2.5 2.5M12 12v5"></path>
            </svg>
          </div>
          <h3 class="card__title">Cloud Engineering</h3>
          <p class="card__body">
            Infrastructure, DevOps, and Kubernetes that keep your product reliable as
            it grows.
          </p>
        </article>

        <article class="card reveal">
          <div class="card__icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
              <rect x="7" y="3" width="10" height="18" rx="2.5"></rect>
              <path d="M11 18h2"></path>
            </svg>
          </div>
          <h3 class="card__title">Mobile Engineering</h3>
          <p class="card__body">
            Native and cross-platform apps, held to the same product quality your
            users expect.
          </p>
        </article>
      </div>
    </section>

    <!-- ================= HOW WE WORK ================= -->
    <section class="how" id="work">
      <div class="section__head">
        <p class="eyebrow reveal">How we work</p>
        <h2 class="section__title reveal">A partner, not a vendor.</h2>
        <p class="section__lead reveal">
          We work as part of your team — from understanding the problem to owning the
          result in production.
        </p>
      </div>

      <ol class="steps">
        <li class="step reveal">
          <span class="step__num">01</span>
          <h3 class="step__title">Understand the product</h3>
          <p class="step__body">
            We start with your users and your goals — not a tech spec. You get a team
            that understands what you're building and why.
          </p>
        </li>
        <li class="step reveal">
          <span class="step__num">02</span>
          <h3 class="step__title">Add senior engineers to your team</h3>
          <p class="step__body">
            Experienced engineers work directly in your codebase, tools, and standups —
            as an extension of your team, not a handoff.
          </p>
        </li>
        <li class="step reveal">
          <span class="step__num">03</span>
          <h3 class="step__title">Ship to production</h3>
          <p class="step__body">
            We build in short cycles and ship real, working software early — AI
            features included — not slideware or throwaway prototypes.
          </p>
        </li>
        <li class="step reveal">
          <span class="step__num">04</span>
          <h3 class="step__title">Own the outcome</h3>
          <p class="step__body">
            After launch we stay accountable for reliability, iteration, and results —
            because the work isn't finished when it ships.
          </p>
        </li>
      </ol>
    </section>

    <!-- ================= CASE STUDIES / PROOF ================= -->
    <section class="proof" id="proof">
      <div class="section__head">
        <p class="eyebrow reveal">Proof</p>
        <h2 class="section__title reveal">We'd rather show you the work.</h2>
        <p class="section__lead reveal">
          No stock logos or invented numbers. Detailed case studies are shared
          directly with prospective partners — here's the kind of engineering we
          take on.
        </p>
      </div>

      <div class="cases">
        <article class="case reveal">
          <p class="case__cat">AI in production</p>
          <h3 class="case__title">LLM features that hold up under real use</h3>
          <p class="case__body">
            RAG pipelines, copilots, and agent workflows taken from prototype to
            reliable, monitored production systems — with the guardrails to run safely
            at scale.
          </p>
          <ul class="case__tags">
            <li>LLMs</li><li>Python</li><li>Vector search</li><li>TypeScript</li>
          </ul>
        </article>

        <article class="case reveal">
          <p class="case__cat">Scale &amp; reliability</p>
          <h3 class="case__title">Systems that stay fast as load grows</h3>
          <p class="case__body">
            High-throughput backends and data platforms architected to hold up as
            traffic, data, and complexity increase — without rewrites every year.
          </p>
          <ul class="case__tags">
            <li>Kubernetes</li><li>Postgres</li><li>Go</li><li>Event streaming</li>
          </ul>
        </article>

        <article class="case reveal">
          <p class="case__cat">Modernization</p>
          <h3 class="case__title">Legacy code moved to a modern stack</h3>
          <p class="case__body">
            Aging codebases migrated to maintainable, well-tested architectures —
            incrementally, without stopping the business or breaking production.
          </p>
          <ul class="case__tags">
            <li>React</li><li>Node</li><li>Cloud</li><li>CI/CD</li>
          </ul>
        </article>

        <article class="case reveal">
          <p class="case__cat">Zero to one</p>
          <h3 class="case__title">New products built end to end</h3>
          <p class="case__body">
            Web, cloud, and mobile products designed and shipped from scratch with a
            senior team from day one — the first version built to be built on.
          </p>
          <ul class="case__tags">
            <li>Next.js</li><li>Swift</li><li>Kotlin</li><li>AWS</li>
          </ul>
        </article>
      </div>

      <div class="proof__cta reveal">
        <p>Working on something similar?</p>
        <a class="btn btn--solid" href="#contact">Request relevant case studies</a>
      </div>
    </section>

    <!-- ================= CONTACT ================= -->
    <section class="contact" id="contact">
      <div class="contact__grid">
        <div class="contact__intro" id="contact-intro">
          <p class="eyebrow reveal" id="contact-eyebrow">Start a project</p>
          <h2 class="section__title reveal" id="contact-title">Let's talk about what you're building.</h2>
          <p class="section__lead reveal" id="contact-lead">
            Tell us about your product and where you need engineering help. You'll
            talk with an engineer who can actually dig into the problem — not a sales
            team.
          </p>
          <ul class="contact__expect reveal" id="contact-expect">
            <li>A technical conversation, not a pitch</li>
            <li>An honest view of how we'd approach it</li>
            <li>Clear next steps if it's a fit</li>
          </ul>
        </div>

        <form class="contact__form reveal" id="contact-form" novalidate>
          <div class="field">
            <label for="cf-name">Name</label>
            <input id="cf-name" name="name" type="text" autocomplete="name" required />
          </div>
          <div class="field">
            <label for="cf-email">Work email</label>
            <input id="cf-email" name="email" type="email" autocomplete="email" required />
          </div>
          <div class="field">
            <label for="cf-company">Company <span class="opt">(optional)</span></label>
            <input id="cf-company" name="company" type="text" autocomplete="organization" />
          </div>
          <div class="field">
            <label for="cf-message" id="cf-message-label">What are you building, and where do you need engineers?</label>
            <textarea id="cf-message" name="message" rows="4" required></textarea>
          </div>
          <input type="text" name="_gotcha" class="hp" tabindex="-1" autocomplete="off" aria-hidden="true" />
          <button class="btn btn--solid btn--lg" type="submit" id="cf-submit">Start the conversation</button>
          <p class="form__note" role="status" aria-live="polite"></p>
        </form>
      </div>
    </section>

    <!-- ================= JOIN THE NETWORK ================= -->
    <section class="network" id="engineers">
      <div class="network__panel">
        <div class="network__intro">
          <p class="eyebrow reveal">For engineers</p>
          <h2 class="section__title reveal">Build meaningful AI products, with engineers worldwide.</h2>
          <p class="section__lead reveal">
            Cowork Development is a global community of experienced engineers — not a
            freelancer marketplace. Work on ambitious AI products alongside people who
            care about craft, wherever you are.
          </p>
          <ul class="network__list reveal">
            <li class="network__item">
              <h3>Meaningful work</h3>
              <p>Hard AI and product problems that actually ship.</p>
            </li>
            <li class="network__item">
              <h3>A global peer network</h3>
              <p>Experienced engineers across every timezone.</p>
            </li>
            <li class="network__item">
              <h3>Remote-first</h3>
              <p>Work from anywhere, on your schedule.</p>
            </li>
            <li class="network__item">
              <h3>Real ownership</h3>
              <p>Own what you build, from architecture to production.</p>
            </li>
          </ul>
          <a class="btn btn--solid btn--lg reveal" href="/apply">Join the network</a>
          <p class="network__note reveal">For experienced engineers — tell us what you've built.</p>
        </div>

        <div class="network__visual reveal" aria-hidden="true">
          <canvas id="globe" class="globe"></canvas>
        </div>
      </div>
    </section>
  </main>

  <!-- ================= FOOTER ================= -->
  <footer class="footer">
    <div class="footer__inner">
      <div class="footer__brand">
        <a class="wordmark" href="/" aria-label="Cowork Development home">
          c<span class="wordmark__dot" aria-hidden="true"></span>work<span class="wordmark__suffix">Development</span>
        </a>
        <p class="footer__tag">
          AI product engineering partner. Experienced engineers building
          AI-powered products with your team.
        </p>
        <a class="btn btn--solid" href="#contact">Start a project</a>
      </div>

      <nav class="footer__cols" aria-label="Footer">
        <div class="footer__col">
          <h4>Company</h4>
          <a href="#services">What we build</a>
          <a href="#work">How we work</a>
          <a href="#proof">Work</a>
          <a href="#contact">Contact</a>
        </div>
        <div class="footer__col">
          <h4>Capabilities</h4>
          <a href="#services">AI Product Engineering</a>
          <a href="#services">Product Engineering</a>
          <a href="#services">Cloud Engineering</a>
          <a href="#services">Mobile Engineering</a>
        </div>
        <div class="footer__col">
          <h4>Connect</h4>
          <a href="mailto:contact@cowork.dev">contact@cowork.dev</a>
          <a href="https://www.linkedin.com/company/cowork-dev" target="_blank" rel="noopener noreferrer">LinkedIn</a>
          <a href="https://www.facebook.com/cowork.dev" target="_blank" rel="noopener noreferrer">Facebook</a>
        </div>
      </nav>
    </div>

    <div class="footer__bar">
      <span>© 2026 Cowork Development. All rights reserved.</span>
    </div>
  </footer>
`;
